import { Typography } from "antd";
import { companyBlock, intro, lastUpdated, legalTitle, sectionBody, sectionHeading } from "../LegalPage.styles.ts";

const { Title, Paragraph, Text } = Typography;

export const TermsEn = () => (
  <article>
    <Title className={legalTitle} level={1}>
      Terms of Use
    </Title>
    <Text className={lastUpdated}>Last updated: 6 May 2026</Text>

    <Paragraph className={intro}>
      These Terms of Use (“Terms”) govern your access to and use of the Calculabel service (the “Service”) provided by{" "}
      <strong>Фууд Сейфти Консултинг ЕООД</strong> (“Calculabel”, “we”, “us”), a limited liability company registered in
      Bulgaria under EIK 204435984, with seat at ул. Тракия № 34, 1527 София. By creating an account or otherwise using
      the Service, you agree to these Terms. If you do not agree, please do not use the Service.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      1. Acceptance and scope
    </Title>
    <Paragraph className={sectionBody}>
      These Terms form a binding agreement between you and Calculabel. They apply to all visitors and registered users
      of the Service. If you accept these Terms on behalf of an organisation (a team), you represent that you are
      authorised to bind that organisation, and “you” refers to both you and the organisation.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      2. The Service
    </Title>
    <Paragraph className={sectionBody}>
      Calculabel is a software-as-a-service platform for food professionals and home cooks. It includes recipe and
      ingredient management, nutritional value calculation, ingredient and producer tracking, food and wine label
      generation, QR-code label creation, purchase tracking, and meal planning. The exact set of features depends on
      your plan, and we may add, modify, or discontinue features over time as described in these Terms.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      3. Account and registration
    </Title>
    <Paragraph className={sectionBody}>
      To use most features you must register an account. We use passwordless authentication via short-lived sign-in
      links sent to your email. You are responsible for keeping your email account secure, for the confidentiality of
      any sign-in link you receive, and for all activity that occurs under your account. Notify us immediately at{" "}
      <a href="mailto:support@calculabel.com">support@calculabel.com</a> if you suspect unauthorised access.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      4. Your content
    </Title>
    <Paragraph className={sectionBody}>
      You retain all rights to the content you create or upload to the Service (“User Content”), including recipes,
      ingredients, producers, files, and labels. By using the Service you grant Calculabel a worldwide, non-exclusive,
      royalty-free licence to host, store, reproduce, transmit, display, and process your User Content solely as
      necessary to operate and provide the Service to you and your team. You warrant that you have all rights necessary
      to upload User Content and that it does not infringe any third party’s rights or violate any law.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      5. Acceptable use
    </Title>
    <div className={sectionBody}>
      <Paragraph>You agree not to:</Paragraph>
      <ul>
        <li>use the Service for any unlawful, fraudulent, infringing, or harmful purpose;</li>
        <li>upload content that is illegal, defamatory, or violates third-party rights;</li>
        <li>attempt to gain unauthorised access to the Service, accounts, or underlying systems;</li>
        <li>scrape, mass-download, or reverse-engineer the Service except as permitted by mandatory law;</li>
        <li>impersonate another person or misrepresent your affiliation;</li>
        <li>interfere with or disrupt the integrity, security, or performance of the Service;</li>
        <li>use the Service to send spam, malware, or other harmful traffic;</li>
        <li>circumvent any technical limitation, rate limit, or access control.</li>
      </ul>
    </div>

    <Title className={sectionHeading} level={4}>
      6. Plans and billing
    </Title>
    <Paragraph className={sectionBody}>
      Calculabel currently offers Free, Basic, and All-Inclusive plans. Pricing for paid plans will be announced on the
      pricing page and presented at sign-up. Once paid plans are active, fees are payable in advance for the selected
      billing cycle, and renew automatically unless cancelled before the renewal date. We may change plan features and
      pricing with reasonable advance notice. Statutory consumer rights of withdrawal, where applicable under EU and
      Bulgarian law, are not affected by these Terms.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      7. Service availability and changes
    </Title>
    <Paragraph className={sectionBody}>
      We aim to keep the Service available on a best-effort basis but do not commit to any specific uptime at this
      stage. We may schedule planned maintenance, and unplanned downtime may occur. We may add, modify, or discontinue
      features. For changes that materially affect paid features, we will give reasonable advance notice.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      8. Important disclaimer — nutrition values and food labelling
    </Title>
    <Paragraph className={sectionBody}>
      <strong>
        Nutrition values, allergen indications, and other label content produced by the Service are calculated estimates
        based on the ingredient data, recipes, and reference databases that you or third parties provide. They are
        provided for informational and operational purposes only and are not certified laboratory analyses.
      </strong>
    </Paragraph>
    <Paragraph className={sectionBody}>
      <strong>
        You are solely responsible for the accuracy, completeness, and legal compliance of any food or wine label,
        nutrition declaration, ingredient list, allergen statement, QR-linked content, or other regulated information
        produced via the Service, including compliance with Regulation (EU) No 1169/2011 on the provision of food
        information to consumers and any applicable national rules.
      </strong>{" "}
      The Service is provided “as is” and “as available”, without warranty of merchantability, fitness for a particular
      purpose, or fitness for regulatory or labelling purposes, to the maximum extent permitted by law.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      9. Limitation of liability
    </Title>
    <Paragraph className={sectionBody}>
      To the fullest extent permitted by Bulgarian law, Calculabel shall not be liable for any indirect, incidental,
      special, consequential, or punitive damages, lost profits, lost data, or loss of goodwill arising out of or in
      connection with your use of the Service. Our aggregate liability for any claim under or in connection with these
      Terms shall not exceed the total amount you have paid us for the Service during the twelve (12) months immediately
      preceding the event giving rise to the claim, or, if you are on a free plan, fifty euro (€50). Nothing in these
      Terms limits liability that cannot be limited under mandatory Bulgarian law.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      10. Termination
    </Title>
    <Paragraph className={sectionBody}>
      You may close your account at any time. We may suspend or terminate your access if you materially breach these
      Terms, if required by law, or if continued provision creates a security or legal risk. Upon termination your right
      to use the Service ends immediately. Sections that by their nature should survive (User Content licence to the
      extent needed for backups, disclaimers, limitation of liability, and governing law) will survive termination.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      11. Governing law and jurisdiction
    </Title>
    <Paragraph className={sectionBody}>
      These Terms are governed by the laws of the Republic of Bulgaria, without regard to its conflict-of-laws rules.
      Any dispute arising out of or in connection with these Terms or the Service shall be subject to the exclusive
      jurisdiction of the competent courts of Sofia, Bulgaria, except where mandatory consumer-protection rules grant
      you the right to bring proceedings in your country of residence.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      12. Changes to these Terms
    </Title>
    <Paragraph className={sectionBody}>
      We may update these Terms from time to time. The “Last updated” date at the top will reflect the latest revision.
      For material changes we will provide reasonable advance notice — for example, an in-app banner or an email to
      registered users. Your continued use of the Service after the effective date of an update means that you accept
      the updated Terms.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      13. Contact
    </Title>
    <Paragraph className={sectionBody}>
      Questions about these Terms? Write to <a href="mailto:support@calculabel.com">support@calculabel.com</a>.
    </Paragraph>

    <Paragraph className={companyBlock}>
      Service provider: Фууд Сейфти Консултинг ЕООД
      <br />
      ЕИК / UIC: 204435984
      <br />
      VAT (ДДС №): BG204435984
      <br />
      Address: ул. Тракия № 34, 1527 София, България
      <br />
      Email: <a href="mailto:support@calculabel.com">support@calculabel.com</a>
    </Paragraph>
  </article>
);
